import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography, Box, Alert, Paper } from '@mui/material';
import {
  Users as UserIcon,
  PackageCheck as InventoryIcon,
  AlertCircle as OutOfStockIcon,
  Share2 as ShareIcon,
  EyeOff as NoShareIcon,
  Truck as ShippingIcon,
  CheckCircle2 as DeliveredIcon,
  Hourglass as PreparingIcon,
  XCircle as CancelIcon,
  RotateCcw as ReturnIcon,
  RefreshCw as ExchangeIcon,
} from 'lucide-react';
import { Title } from 'react-admin';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { DashboardResponse, DashboardStats, DailySales } from '../types/dashboard';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [dailySales, setDailySales] = useState<DailySales[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 실제 Admin API 호출
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/v1/admin/dashboard`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`API 오류: ${response.status}`);
        }

        const data: DashboardResponse = await response.json();

        setStats(data.stats);
        setDailySales(data.dailySales);
        setLoading(false);
      } catch (error: any) {
        console.error('대시보드 데이터 조회 실패:', error);
        setError(error.message || '데이터를 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // 네이버 스마트 스토어 스타일의 심플한 통계 카드
  const StatCard = ({ title, value, icon, color, unit = '', link }: any) => (
    <Paper
      variant="outlined"
      onClick={() => link && navigate(link)}
      sx={{
        p: 2,
        height: '100%',
        borderColor: '#e0e0e0',
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: link ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: color || '#b0b0b0',
          bgcolor: '#fafafa',
          transform: link ? 'translateY(-2px)' : 'none',
          boxShadow: link ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
        }
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
          {title}
        </Typography>
        {icon && (
          <Box
            sx={{
              color: color || '#757575',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.5,
              borderRadius: '50%',
              bgcolor: color ? `${color}10` : 'transparent'
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
      <Box display="flex" alignItems="baseline" gap={0.5}>
        <Typography variant="h5" fontWeight={700} color={color || "#333"}>
          {value?.toLocaleString() || 0}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {unit}
        </Typography>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box p={3}>
        <Typography>대시보드 데이터를 불러오는 중...</Typography>
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Box p={3}>
        <Title title="대시보드" />
        <Alert severity="error">
          {error || '데이터를 불러올 수 없습니다.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f6f8', minHeight: '100vh' }}>
      <Title title="대시보드" />

      {/* 상품 현황 */}
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1.1rem', color: '#333' }}>
          상품 현황
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6} md={3}>
            <StatCard title="정상 (판매중)" value={stats.productsActive} icon={<InventoryIcon />} color="#4CAF50" unit="개" link="/products?filter=%7B%22isActive%22%3Atrue%7D" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="품절" value={stats.productsOutOfStock} icon={<OutOfStockIcon />} color="#F44336" unit="개" link="/products?filter=%7B%22stock%22%3A0%7D" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="네이버 노출" value={stats.productsNaverExposed} icon={<ShareIcon />} color="#03C75A" unit="개" link="/products?filter=%7B%22naverShoppingExposed%22%3Atrue%7D" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="네이버 미노출" value={stats.productsNaverNotExposed} icon={<NoShareIcon />} color="#9E9E9E" unit="개" link="/products?filter=%7B%22naverShoppingExposed%22%3Afalse%7D" />
          </Grid>
        </Grid>
      </Box>

      {/* 주문 현황 */}
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1.1rem', color: '#333' }}>
          주문 현황
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <StatCard title="배송준비" value={stats.ordersPreparing} icon={<PreparingIcon />} color="#FF9800" unit="건" link="/orders?filter=%7B%22status%22%3A%22PROCESSING%22%7D" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="배송중" value={stats.ordersShipping} icon={<ShippingIcon />} color="#2196F3" unit="건" link="/orders?filter=%7B%22status%22%3A%22SHIPPED%22%7D" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="배송완료" value={stats.ordersDelivered} icon={<DeliveredIcon />} color="#4CAF50" unit="건" link="/orders?filter=%7B%22status%22%3A%22DELIVERED%22%7D" />
          </Grid>
        </Grid>
      </Box>

      {/* 클레임 현황 */}
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1.1rem', color: '#333' }}>
          클레임 현황
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <StatCard title="취소요청" value={stats.claimsCancel} icon={<CancelIcon />} color="#F44336" unit="건" link="/orders?filter=%7B%22status%22%3A%22CANCELLED%22%7D" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="반품요청" value={stats.claimsReturn} icon={<ReturnIcon />} color="#FF5722" unit="건" link="/orders?filter=%7B%22status%22%3A%22REFUNDED%22%7D" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="교환요청" value={stats.claimsExchange} icon={<ExchangeIcon />} color="#795548" unit="건" link="/orders" />
          </Grid>
        </Grid>
      </Box>

      {/* 회원 현황 */}
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1.1rem', color: '#333' }}>
          회원 현황
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6} md={6}>
            <StatCard title="전체 회원" value={stats.usersTotal} icon={<UserIcon />} color="#673AB7" unit="명" link="/users" />
          </Grid>
          <Grid item xs={6} md={6}>
            <StatCard title="오늘 가입" value={stats.usersNewToday} icon={<UserIcon />} color="#9C27B0" unit="명" link="/users" />
          </Grid>
        </Grid>
      </Box>

      {/* 차트 섹션 */}
      <Grid container spacing={2}>
        {/* 일별 매출 현황 */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 0, borderColor: '#e0e0e0', height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={700} color="#333">
                매출 현황
              </Typography>
              <Typography variant="caption" color="textSecondary">
                최근 7일
              </Typography>
            </Box>
            <Box height={300}>
              {dailySales.some(item => item.sales > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailySales} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#888' }}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#888' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `${value / 10000}만`}
                    />
                    <Tooltip
                      formatter={(value: any) => [`${value.toLocaleString()}원`, '매출']}
                      contentStyle={{ border: '1px solid #eee', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke="#03C75A" // Naver Green
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#03C75A', strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography variant="body2" color="textSecondary">데이터가 없습니다</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;